// Strategic Goals & OKRs widget — pulls /api/goals/summary/stats and the
// first 5 goals so users see a quick rollup and can deep-link to the page.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Target, ArrowRight, CircleDot } from 'lucide-react';
import { progressBarColor } from '@/utils/styleHelpers';
import { BRAND, GOAL_CAT_ICONS, GOAL_STATUS_COLORS } from './_constants';

export const GoalsWidget = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [goals, setGoals] = useState([]);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/goals/summary/stats`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/goals?status=`, { headers }).then(r => r.ok ? r.json() : []),
    ]).then(([s, g]) => { setStats(s); setGoals(g.slice(0, 5)); }).catch(() => {});
  }, [token]);

  if (!stats || stats.total_objectives === 0) return null;

  const calcProg = (krs) => {
    if (!krs?.length) return 0;
    const valid = krs.filter(kr => kr.target > 0);
    if (!valid.length) return 0;
    return Math.round(valid.reduce((s, kr) => s + Math.min((kr.current / kr.target) * 100, 100), 0) / valid.length);
  };

  return (
    <Card data-testid="goals-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5" style={{ color: BRAND.cyan }} />
            Goals & OKRs
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/goals" className="text-xs flex items-center gap-1">View All <ArrowRight className="h-3 w-3" /></Link>
          </Button>
        </div>
        <CardDescription>Strategic objectives progress</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="text-center p-2 rounded-lg bg-muted/30">
            <p className="text-lg font-bold">{stats.total_objectives}</p>
            <p className="text-xs text-muted-foreground">Objectives</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-muted/30">
            <p className="text-lg font-bold">{stats.total_key_results}</p>
            <p className="text-xs text-muted-foreground">Key Results</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-muted/30">
            <p className="text-lg font-bold text-emerald-600">{stats.completed_key_results}</p>
            <p className="text-xs text-muted-foreground">KRs Done</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-muted/30">
            <p className="text-lg font-bold" style={{ color: BRAND.cyan }}>{stats.avg_progress}%</p>
            <p className="text-xs text-muted-foreground">Avg Progress</p>
          </div>
        </div>
        <div className="space-y-2.5">
          {goals.map(g => {
            const prog = calcProg(g.key_results);
            const CIcon = GOAL_CAT_ICONS[g.category_key] || CircleDot;
            const sColor = GOAL_STATUS_COLORS[g.status] || '#94a3b8';
            return (
              <Link to="/goals" key={g.goal_id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/40 transition-colors group" data-testid={`goals-widget-item-${g.goal_id}`}>
                <CIcon className="h-4 w-4 flex-shrink-0" style={{ color: sColor }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{g.title}</p>
                  <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden mt-1.5">
                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(prog, 100)}%`, backgroundColor: progressBarColor(prog, BRAND) }} />
                  </div>
                </div>
                <span className="text-xs font-mono font-medium w-10 text-right" style={{ color: sColor }}>{prog}%</span>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
